import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Outlet, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { MobileTopbar, DesktopSidebar } from "./Sidebar";
import BookmarkIcon from "./BookmarkIcon";
import StreakBadge from "./StreakBadge";
import TeacherNotificationBell from "./TeacherNotificationBell";
import MobileTabBar from "./MobileTabBar";
import AnnouncementBanner from "./AnnouncementBanner";
import { useAuth } from "../lib/auth";

// Some mobile browsers (several Android WebViews/browser shells included)
// measure `100dvh` as the LARGEST possible viewport (address bar collapsed)
// on first paint instead of what's actually visible right now — which made
// the frame render a bit taller than the real screen and pushed the
// bottom-pinned tab bar just past the fold until the user scrolled once.
// `window.innerHeight` is the classic, reliable fix for this (unlike
// `visualViewport.height`, it does NOT shrink when the on-screen keyboard
// opens, so it won't make the whole shell resize under an open keyboard).
// The CSS `h-screen h-dvh` classes stay as the pre-JS/no-JS fallback.
function useRealViewportHeight() {
  const [height, setHeight] = useState(null);
  useEffect(() => {
    const measure = () => setHeight(window.innerHeight);
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);
  return height;
}

// The scroller is this inner div, not the window, so the browser's own scroll
// handling never applies: without this, moving to another page (e.g. "Mark
// memorized & continue" → next ayah) kept the old scroll offset and the new
// page looked identical, still parked at the bottom. New page → top; Back/
// Forward → the position that page was left at.
function useScrollPerRoute(scrollerRef, ready) {
  const location = useLocation();
  const navType = useNavigationType();
  const saved = useRef(new Map());
  const currentKey = useRef(location.key);
  const prev = useRef({ key: location.key, pathname: location.pathname });

  // Record the offset as the user scrolls (not at navigation time — by then
  // the next page has already replaced the DOM and the browser may have
  // clamped scrollTop, so reading it then isn't the old page's real offset).
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const onScroll = () => saved.current.set(currentKey.current, el.scrollTop);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollerRef, ready]);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const last = prev.current;
    if (last.key === location.key) return undefined;
    currentKey.current = location.key;
    prev.current = { key: location.key, pathname: location.pathname };

    if (navType === "POP") {
      const target = saved.current.get(location.key) || 0;
      el.scrollTop = target;
      // Page content usually loads after mount, so re-apply as it grows.
      const timers = [120, 400, 900].map((ms) => setTimeout(() => {
        if (Math.abs(el.scrollTop - target) > 2) el.scrollTop = target;
      }, ms));
      return () => timers.forEach(clearTimeout);
    }
    // Same page, just a different ?query (tabs/filters) keeps its position.
    if (last.pathname !== location.pathname) el.scrollTop = 0;
    return undefined;
  }, [location, navType, scrollerRef, ready]);
}

export default function AppLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const frameHeight = useRealViewportHeight();
  const scrollerRef = useRef(null);
  useScrollPerRoute(scrollerRef, !loading && !!user);
  if (loading) {
    return (
      <div className="min-h-screen bg-sand-200 grid place-items-center">
        <div className="animate-pulse-soft font-display text-forest-700 text-lg">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return (
    // Outer gutter — fills the real browser viewport at any width. Flat
    // sand-200 so the app frame below reads as a centered card on a plain
    // field (visible empty space either side on a wide monitor, none at
    // all on an actual phone where the frame already fills the screen).
    <div className="min-h-screen bg-sand-200">
      {/* The app always lives inside this fixed-width "phone frame" now —
          on a laptop or a phone alike — instead of a responsive desktop-
          sidebar-vs-mobile-topbar split. No sm:/md:/lg:/xl: anywhere in
          this file: real-viewport breakpoints are meaningless once content
          always lives in a fixed-width card — this is the same width on a
          phone and on a 4K monitor.

          `relative` + `transform-gpu` both matter:
          - `relative` is the containing block for `position: absolute`
            descendants (the hamburger drawer, MobileTabBar, and two
            page-level floating buttons in MyQuran.jsx/TafsirReader.jsx).
          - `transform-gpu` is a real `transform`, which ALSO makes this
            the containing block for `position: fixed` descendants — several
            pages (Community, AdminPanel, OrgAdmin, TeacherProfile,
            SocialShare) have their own plain `fixed inset-0` modal
            backdrops, and this one class anchors/clips all of them to the
            visible card too, for free. Don't remove it as an unused-looking
            class — those modals would silently go back to covering the
            entire real browser window.
            (Radix/shadcn Dialog/AlertDialog/Sheet and sonner's Toaster all
            portal straight to document.body, outside this subtree entirely,
            so they're unaffected either way and correctly keep covering the
            real full viewport, same as always.)

          `h-screen h-dvh overflow-hidden` pins this box to exactly one
          viewport tall, non-scrolling — `dvh` is the enhancement (correctly
          accounts for mobile browser chrome show/hide), `h-screen` is the
          fallback for browsers that don't support the dvh unit (an
          unsupported value is simply invalid and ignored, so h-screen's
          100vh silently wins there instead). As a flex column, MobileTopbar
          and MobileTabBar are just its first/last children around one
          overflow-y-auto middle child — so they can't end up anywhere but
          the top/bottom of the visible frame, whether a given page is one
          screen or ten. */}
      <div
        className="relative mx-auto flex h-screen h-dvh w-full max-w-[var(--app-frame-w)] flex-col lg:flex-row overflow-hidden geo-bg transform-gpu"
        style={frameHeight ? { height: `${frameHeight}px` } : undefined}
      >
        <DesktopSidebar />
        <div className="flex flex-1 min-h-0 min-w-0 flex-col">
          <MobileTopbar />
          {/* Desktop top bar (lg+): bookmark, streak and notifications; the sidebar carries the rest. */}
          <div className="hidden lg:flex shrink-0 items-center justify-end gap-1 px-10 pt-5" data-testid="desktop-topbar">
            <BookmarkIcon />
            <StreakBadge />
            <TeacherNotificationBell />
          </div>

          <div ref={scrollerRef} id="app-scroll" className="flex-1 min-h-0 overflow-y-auto p-5 lg:px-10 lg:pt-4 lg:pb-10 pb-[calc(var(--mobile-tabbar-h)_+_env(safe-area-inset-bottom)_+_1.25rem)]">
            <div className="mx-auto w-full lg:max-w-5xl">
              <AnnouncementBanner />
              <Outlet />
            </div>
          </div>

          <MobileTabBar />
        </div>
      </div>
    </div>
  );
}
